import type { IncidentKpis, IncidentOpsQueuesResponse } from '../services/incidentsApi';
import type { SafetySummary } from '../services/metricsApi';
import type { OverviewControlSummary } from '../domain/overview/overviewTypes';

const CACHE_PREFIX = 'edgentik.overview.snapshot.v1';
/** Show cached snapshot up to 24h; still refreshed in background on every visit. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function readStorageItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
    window.sessionStorage.removeItem(key);
  } catch {
    // quota / private mode
  }
}

function removeStorageItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export type OverviewSnapshotCachePayload = {
  version: 1;
  cachedAt: string;
  incidentKpis: IncidentKpis | null;
  controlSummary: OverviewControlSummary;
  activeAlertCount: number;
  unresolvedIncidentsInScope: number | null;
  safetySummary: Pick<
    SafetySummary,
    'cameras_online' | 'scoped_open_incidents_total' | 'avg_latency_ms' | 'prometheus_firing_alerts'
  > | null;
  opsQueues: IncidentOpsQueuesResponse | null;
  policiesCount: number;
  devicesOnline: number;
  devicesOffline: number;
  devicesUnknown: number;
  devicesInScope: number;
};

export function getOverviewSnapshotCacheKey(orgId: string, siteId: string): string {
  return `${CACHE_PREFIX}:${orgId}:${siteId}`;
}

export function readOverviewSnapshotCache(
  orgId: string,
  siteId: string,
): OverviewSnapshotCachePayload | null {
  if (!orgId || !siteId || typeof window === 'undefined') return null;
  try {
    const raw = readStorageItem(getOverviewSnapshotCacheKey(orgId, siteId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OverviewSnapshotCachePayload;
    if (!parsed || parsed.version !== 1 || !parsed.cachedAt) return null;
    const age = Date.now() - Date.parse(parsed.cachedAt);
    if (!Number.isFinite(age) || age > MAX_AGE_MS) return null;
    if (!parsed.controlSummary?.loaded) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeOverviewSnapshotCache(
  orgId: string,
  siteId: string,
  payload: Omit<OverviewSnapshotCachePayload, 'version' | 'cachedAt'>,
): void {
  if (!orgId || !siteId || typeof window === 'undefined') return;
  if (!payload.controlSummary?.loaded) return;
  try {
    const entry: OverviewSnapshotCachePayload = {
      version: 1,
      cachedAt: new Date().toISOString(),
      ...payload,
    };
    writeStorageItem(getOverviewSnapshotCacheKey(orgId, siteId), JSON.stringify(entry));
  } catch {
    // quota / private mode
  }
}

export function clearOverviewSnapshotCache(orgId: string, siteId: string): void {
  if (!orgId || !siteId || typeof window === 'undefined') return;
  try {
    removeStorageItem(getOverviewSnapshotCacheKey(orgId, siteId));
  } catch {
    // ignore
  }
}
